import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import type { User } from "@harmonyso/users-sdk";

interface LoginIdSelectorProps {
  user: User;
  tenantId: string;
  selectedLoginId: string | null;
  onLoginIdChange: (loginId: string) => void;
}

export function LoginIdSelector({
  user,
  tenantId,
  selectedLoginId,
  onLoginIdChange,
}: LoginIdSelectorProps) {
  const loginIds = user.login_ids ?? [];

  // If no login IDs, fall back to user_id
  if (!loginIds.length) {
    if (!selectedLoginId) {
      onLoginIdChange(user.user_id);
    }
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">Login ID</label>
        <Badge variant="outline" className="font-mono">
          {user.user_id} (user ID)
        </Badge>
      </div>
    );
  }

  // If only one login ID, show it directly
  if (loginIds.length === 1) {
    if (!selectedLoginId) {
      onLoginIdChange(loginIds[0]);
    }
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">Login ID</label>
        <Badge variant="outline" className="font-mono">
          {loginIds[0]}
        </Badge>
      </div>
    );
  }

  // Multiple login IDs - let user choose
  const getLoginIdDisplay = (loginId: string) => {
    // Try to find tenant-related login ID (best effort)
    if (loginId.includes(tenantId)) {
      return `${loginId} (tenant)`;
    }
    if (loginId.includes("@")) {
      return `${loginId} (email)`;
    }
    return loginId;
  };

  // Auto-select best option if none selected
  if (!selectedLoginId) {
    // Try to find tenant-related login ID first
    const tenantRelated = loginIds.find((id) => id.includes(tenantId));
    if (tenantRelated) {
      onLoginIdChange(tenantRelated);
    } else {
      // Default to first one
      onLoginIdChange(loginIds[0]);
    }
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">
        Login ID ({loginIds.length} available)
      </label>
      <Select value={selectedLoginId ?? ""} onValueChange={onLoginIdChange}>
        <SelectTrigger className="font-mono text-sm">
          <SelectValue placeholder="Select login ID" />
        </SelectTrigger>
        <SelectContent>
          {loginIds.map((loginId) => (
            <SelectItem
              key={loginId}
              value={loginId}
              className="font-mono text-sm"
            >
              {getLoginIdDisplay(loginId)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
